import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaWifi,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['UPI', 'Net Banking', 'Credit Card', 'Debit Card', 'Auto Pay', 'Cash'];
const PLAN_TYPES = ['Fiber Broadband', 'Wireless Broadband', 'DSL', 'Leased Line', 'FTTH'];

const TEMPLATES = [
  { id: 'classic', label: 'Classic Invoice', accent: '#2563eb' },
  { id: 'modern', label: 'Modern Banner', accent: '#0891b2' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827' },
  { id: 'sidebar', label: 'Side Strip', accent: '#7c3aed' },
  { id: 'compact', label: 'Compact GST', accent: '#16a34a' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const addDaysDMY = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const generateBillNumber = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(100000 + Math.random() * 900000);
  return `INET-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newCharge = (label = '', amount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  label,
  amount,
});

const InternetBillGenerator = () => {
  const [template, setTemplate] = useState('classic');
  const [logo, setLogo] = useState(null);

  const [isp, setIsp] = useState({
    name: 'FiberNet Communications Pvt. Ltd.',
    address: 'Tower 4, Tech Park, Sector 18, Noida 201301',
    phone: '1800-200-1234',
    email: 'support@fibernet.in',
    gstin: '09ABCDE1234F1Z5',
    website: 'www.fibernet.in',
  });

  const [customer, setCustomer] = useState({
    name: 'Rahul Sharma',
    address: 'B-204, Green Heights, Sector 50, Noida 201301',
    phone: '+91 98765 43210',
    accountNo: 'FBN-1004521',
    customerId: 'CUST-78421',
    email: 'rahul.sharma@example.com',
  });

  const [plan, setPlan] = useState({
    name: 'Fiber Pro 200 Mbps',
    type: 'Fiber Broadband',
    speed: '200 Mbps',
    dataLimit: 'Unlimited',
  });

  const [meta, setMeta] = useState({
    billNo: generateBillNumber(),
    billDate: todayDMY(),
    dueDate: addDaysDMY(15),
    periodFrom: addDaysDMY(-30),
    periodTo: todayDMY(),
    paymentMode: 'UPI',
  });

  const [charges, setCharges] = useState([
    newCharge('Monthly Rental - Fiber Pro 200 Mbps', 999),
    newCharge('OTT Add-on (Hotstar + Prime)', 149),
    newCharge('Static IP Charge', 100),
  ]);

  const [tax, setTax] = useState({
    cgst: 9,
    sgst: 9,
    previousBalance: 0,
    paymentReceived: 0,
  });

  const updateCharge = (id, patch) =>
    setCharges((list) => list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const addCharge = () =>
    setCharges((list) => [...list, newCharge('New Charge', 0)]);
  const removeCharge = (id) =>
    setCharges((list) => list.filter((c) => c.id !== id));

  const totals = useMemo(() => {
    const subtotal = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
    const cgstAmt = subtotal * (Number(tax.cgst || 0) / 100);
    const sgstAmt = subtotal * (Number(tax.sgst || 0) / 100);
    const currentCharges = subtotal + cgstAmt + sgstAmt;
    const totalDue = currentCharges + Number(tax.previousBalance || 0) - Number(tax.paymentReceived || 0);
    const totalDueRounded = Math.round(totalDue);
    const roundOff = totalDueRounded - totalDue;
    return {
      subtotal,
      cgstAmt,
      sgstAmt,
      totalTax: cgstAmt + sgstAmt,
      currentCharges,
      totalDue,
      totalDueRounded,
      roundOff,
    };
  }, [charges, tax]);

  // ---------- PDF helpers ----------
  const drawChargesTable = (doc, startY, pageW, margin, accent) => {
    const [r, g, b] = hexToRgbArr(accent);
    const tableW = pageW - margin * 2;
    const descW = tableW - 40;
    const amtW = 40;

    // Header
    doc.setFillColor(r, g, b);
    doc.rect(margin, startY, tableW, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Description', margin + 3, startY + 5.5);
    doc.text('Amount (Rs.)', margin + tableW - 3, startY + 5.5, { align: 'right' });

    let y = startY + 8;
    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);

    charges.forEach((c, i) => {
      if (i % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y, tableW, 7, 'F');
      }
      const wrapped = doc.splitTextToSize(String(c.label), descW - 6);
      doc.text(wrapped, margin + 3, y + 4.8);
      doc.text(formatINR(c.amount), margin + tableW - 3, y + 4.8, { align: 'right' });
      y += 7;
    });

    doc.setDrawColor(220, 220, 220);
    doc.rect(margin, startY, tableW, y - startY);
    doc.line(margin + descW, startY, margin + descW, y);
    void amtW;
    return y + 2;
  };

  const drawTotalsBlock = (doc, startY, pageW, margin, accent) => {
    const [r, g, b] = hexToRgbArr(accent);
    const boxW = 90;
    const x = pageW - margin - boxW;
    let y = startY;
    const line = (k, v, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 10 : 9.5);
      doc.setTextColor(40, 40, 40);
      doc.text(k, x, y);
      doc.text(`Rs. ${formatINR(v)}`, x + boxW, y, { align: 'right' });
      y += 5.4;
    };
    line('Subtotal', totals.subtotal);
    line(`CGST @ ${tax.cgst}%`, totals.cgstAmt);
    line(`SGST @ ${tax.sgst}%`, totals.sgstAmt);
    line('Current Charges', totals.currentCharges, true);
    if (Number(tax.previousBalance) !== 0) line('Previous Balance', tax.previousBalance);
    if (Number(tax.paymentReceived) !== 0) line('Payment Received', -tax.paymentReceived);
    if (Math.abs(totals.roundOff) >= 0.005) line('Round Off', totals.roundOff);

    y += 2;
    doc.setFillColor(r, g, b);
    doc.rect(x - 2, y - 4, boxW + 2, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total Due', x, y + 3);
    doc.text(`Rs. ${formatINR(totals.totalDueRounded)}`, x + boxW, y + 3, { align: 'right' });
    return y + 12;
  };

  // ---------- 5 templates ----------
  const renderClassic = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[0].accent;
    const [r, g, b] = hexToRgbArr(accent);

    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, 28, 'F');
    drawPdfLogo(doc, logo, margin, 4, 16, isp.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(isp.name, margin + 20, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(isp.address, margin, 18);
    doc.text(`${isp.phone}  |  ${isp.email}  |  ${isp.website}`, margin, 23);

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('INTERNET BILL', pageW - margin, 14, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`GSTIN: ${isp.gstin}`, pageW - margin, 20, { align: 'right' });

    let y = 36;
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Bill To', margin, y);
    doc.text('Bill Details', pageW / 2 + 6, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.text(customer.name, margin, y);
    const cWrap = doc.splitTextToSize(customer.address, pageW / 2 - margin - 6);
    doc.text(cWrap, margin, y + 5);
    doc.text(`A/C: ${customer.accountNo}`, margin, y + 5 + cWrap.length * 4.6);
    doc.text(`Customer ID: ${customer.customerId}`, margin, y + 10 + cWrap.length * 4.6);
    doc.text(customer.phone, margin, y + 15 + cWrap.length * 4.6);

    const rx = pageW / 2 + 6;
    doc.text(`Bill No:`, rx, y); doc.text(meta.billNo, rx + 30, y);
    doc.text(`Bill Date:`, rx, y + 5); doc.text(meta.billDate, rx + 30, y + 5);
    doc.text(`Due Date:`, rx, y + 10); doc.text(meta.dueDate, rx + 30, y + 10);
    doc.text(`Period:`, rx, y + 15); doc.text(`${meta.periodFrom} - ${meta.periodTo}`, rx + 30, y + 15);
    doc.text(`Plan:`, rx, y + 20); doc.text(`${plan.name} (${plan.speed})`, rx + 30, y + 20);

    y += 36;
    y = drawChargesTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawTotalsBlock(doc, y, pageW, margin, accent);

    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    doc.text('This is a computer-generated invoice and does not require a signature.', pageW / 2, y, { align: 'center' });
    doc.text(`Pay by ${meta.dueDate} to avoid service interruption.`, pageW / 2, y + 4, { align: 'center' });

    return doc;
  };

  const renderModern = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[1].accent;
    const [r, g, b] = hexToRgbArr(accent);

    doc.setFillColor(r, g, b);
    doc.rect(0, 0, pageW, 42, 'F');
    drawPdfLogo(doc, logo, margin, 6, 18, isp.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(isp.name.toUpperCase(), pageW / 2, 16, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(isp.address, pageW / 2, 23, { align: 'center' });
    doc.text(`${isp.phone}  •  ${isp.email}  •  ${isp.website}`, pageW / 2, 29, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`INTERNET BILL — ${meta.billNo}`, pageW / 2, 37, { align: 'center' });

    let y = 50;
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(customer.name, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    const cWrap = doc.splitTextToSize(customer.address, pageW - margin * 2);
    doc.text(cWrap, margin, y + 5);
    y += 5 + cWrap.length * 4.6;
    doc.text(`A/C ${customer.accountNo} • Cust ID ${customer.customerId} • ${customer.phone}`, margin, y + 4);
    doc.text(`Plan: ${plan.name} (${plan.type}, ${plan.speed}, ${plan.dataLimit})`, margin, y + 9);
    doc.text(`Period: ${meta.periodFrom} to ${meta.periodTo}   |   Due: ${meta.dueDate}   |   GSTIN: ${isp.gstin}`, margin, y + 14);

    y += 22;
    y = drawChargesTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawTotalsBlock(doc, y, pageW, margin, accent);

    y += 8;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    doc.text(`Pay via ${meta.paymentMode} • Auto-pay available • Late fee applies after ${meta.dueDate}`, pageW / 2, y, { align: 'center' });

    return doc;
  };

  const renderMinimal = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 16;
    const accent = TEMPLATES[2].accent;

    drawPdfLogo(doc, logo, margin, 8, 14, isp.name, [240, 240, 240]);
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(isp.name, margin + 18, 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    doc.text(`${isp.address}  |  ${isp.phone}  |  ${isp.email}`, margin + 18, 24);
    doc.text(`GSTIN: ${isp.gstin}`, margin + 18, 29);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(margin, 32, pageW - margin, 32);
    doc.setLineWidth(0.2);

    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('INTERNET BILL', margin, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.billNo, pageW - margin, 40, { align: 'right' });

    let y = 48;
    doc.setFontSize(9.5);
    const lh = 5.2;
    const line = (k, v) => {
      doc.setTextColor(110, 110, 110);
      doc.text(k, margin, y);
      doc.setTextColor(20, 20, 20);
      doc.text(String(v), margin + 38, y);
      y += lh;
    };
    line('Customer', customer.name);
    line('A/C No', `${customer.accountNo} (Cust ID ${customer.customerId})`);
    line('Address', customer.address);
    line('Plan', `${plan.name} • ${plan.type} • ${plan.speed} • ${plan.dataLimit}`);
    line('Period', `${meta.periodFrom} to ${meta.periodTo}`);
    line('Bill Date / Due', `${meta.billDate}  /  ${meta.dueDate}`);

    y += 2;
    y = drawChargesTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawTotalsBlock(doc, y, pageW, margin, accent);

    y += 6;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(140, 140, 140);
    doc.text('System-generated invoice. No signature required.', margin, y);

    return doc;
  };

  const renderSidebar = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const accent = TEMPLATES[3].accent;
    const [r, g, b] = hexToRgbArr(accent);
    const sideW = 52;

    doc.setFillColor(r, g, b);
    doc.rect(0, 0, sideW, pageH, 'F');
    drawPdfLogo(doc, logo, (sideW - 22) / 2, 6, 22, isp.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const wrapped = doc.splitTextToSize(isp.name, sideW - 8);
    doc.text(wrapped, 4, 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    let sy = 34 + wrapped.length * 6 + 4;
    doc.text(doc.splitTextToSize(isp.address, sideW - 8), 4, sy); sy += 16;
    doc.text(isp.phone, 4, sy); sy += 5;
    doc.text(isp.email, 4, sy); sy += 5;
    doc.text(isp.website, 4, sy); sy += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('BILL NO', 4, sy); sy += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(meta.billNo, 4, sy); sy += 7;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DUE DATE', 4, sy); sy += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(meta.dueDate, 4, sy); sy += 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('TOTAL DUE', 4, sy); sy += 6;
    doc.setFontSize(14);
    doc.text(`Rs. ${formatINR(totals.totalDueRounded)}`, 4, sy);

    const bodyX = sideW + 8;
    let y = 14;
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Internet Bill', bodyX, y);
    y += 8;

    doc.setFontSize(9.5);
    const lh = 5.4;
    const line = (k, v) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(k, bodyX, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      const v2 = doc.splitTextToSize(String(v), pageW - bodyX - 30);
      doc.text(v2, bodyX + 32, y);
      y += lh * v2.length;
    };
    line('Customer:', customer.name);
    line('A/C No:', customer.accountNo);
    line('Customer ID:', customer.customerId);
    line('Address:', customer.address);
    line('Plan:', `${plan.name} (${plan.speed}, ${plan.dataLimit})`);
    line('Period:', `${meta.periodFrom} - ${meta.periodTo}`);
    line('GSTIN:', isp.gstin);

    y += 3;
    const margin = sideW + 4;
    y = drawChargesTable(doc, y, pageW + margin - 14, margin, accent);
    y += 4;
    y = drawTotalsBlock(doc, y, pageW + margin - 14, margin, accent);

    return doc;
  };

  const renderCompact = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    const accent = TEMPLATES[4].accent;
    const [r, g, b] = hexToRgbArr(accent);

    doc.setDrawColor(r, g, b);
    doc.setLineWidth(1.2);
    doc.line(margin, 12, pageW - margin, 12);
    doc.setLineWidth(0.2);

    drawPdfLogo(doc, logo, margin, 14, 14, isp.name, [240, 240, 240]);
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(isp.name, margin + 18, 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${isp.address}  •  ${isp.phone}  •  GSTIN ${isp.gstin}`, margin + 18, 25);

    doc.setTextColor(r, g, b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`TAX INVOICE — ${meta.billNo}`, pageW - margin, 20, { align: 'right' });

    let y = 32;
    const colW = (pageW - margin * 2) / 2;
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, y, pageW - margin * 2, 28, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.rect(margin, y, pageW - margin * 2, 28);

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(9);
    const lh = 4.6;
    const writeCol = (col, items) => {
      let yy = y + 5;
      items.forEach(([k, v]) => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(90, 90, 90);
        doc.text(k, margin + 3 + col * colW, yy);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(20, 20, 20);
        const v2 = doc.splitTextToSize(String(v), colW - 34);
        doc.text(v2, margin + 30 + col * colW, yy);
        yy += lh * Math.max(1, v2.length);
      });
    };
    writeCol(0, [
      ['Customer:', customer.name],
      ['A/C No:', customer.accountNo],
      ['Cust ID:', customer.customerId],
      ['Phone:', customer.phone],
      ['Address:', customer.address],
    ]);
    writeCol(1, [
      ['Bill Date:', meta.billDate],
      ['Due Date:', meta.dueDate],
      ['Period:', `${meta.periodFrom} - ${meta.periodTo}`],
      ['Plan:', `${plan.name} (${plan.speed})`],
      ['Payment:', meta.paymentMode],
    ]);

    y += 32;
    y = drawChargesTable(doc, y, pageW, margin, accent);
    y += 4;
    y = drawTotalsBlock(doc, y, pageW, margin, accent);

    y += 10;
    const sigW = (pageW - margin * 2 - 10) / 2;
    doc.setDrawColor(180, 180, 180);
    doc.line(margin, y + 14, margin + sigW, y + 14);
    doc.line(pageW - margin - sigW, y + 14, pageW - margin, y + 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text('Customer Acknowledgement', margin, y + 19);
    doc.text('For ' + isp.name, pageW - margin, y + 19, { align: 'right' });

    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    modern: renderModern,
    minimal: renderMinimal,
    sidebar: renderSidebar,
    compact: renderCompact,
  };

  const buildPDF = () => TEMPLATE_RENDERERS[template]();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Internet_Bill_${meta.billNo}.pdf`);
  };

  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  const handleReset = () => {
    setTemplate('classic');
    setMeta({
      billNo: generateBillNumber(),
      billDate: todayDMY(),
      dueDate: addDaysDMY(15),
      periodFrom: addDaysDMY(-30),
      periodTo: todayDMY(),
      paymentMode: 'UPI',
    });
    setCharges([
      newCharge('Monthly Rental - Fiber Pro 200 Mbps', 999),
      newCharge('OTT Add-on (Hotstar + Prime)', 149),
      newCharge('Static IP Charge', 100),
    ]);
    setTax({ cgst: 9, sgst: 9, previousBalance: 0, paymentReceived: 0 });
  };

  const activeTpl = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Internet Bill Generator"
        description="Generate professional internet / broadband bills with GST, plan details and 5 printable templates. Download as PDF."
        keywords="internet bill generator, broadband bill format, isp invoice, gst internet bill, fiber bill pdf"
        canonicalPath="/internet-bill"
        calculatorType="InternetBillGenerator"
        calculatorDescription="Create GST-compliant internet / broadband invoices with itemised charges, plan info and 5 templates."
      />

      <div className="calculator-header">
        <h1>Internet Bill Generator</h1>
        <p>Broadband / ISP invoices with GST and 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Template</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {TEMPLATES.map((t) => {
                const active = t.id === template;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#d1d5db'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#374151',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="ISP Logo" accent={activeTpl.accent} />

          <h3 style={{ marginTop: 16 }}>ISP / Service Provider</h3>
          <div className="input-group">
            <label>ISP Name</label>
            <input type="text" value={isp.name}
              onChange={(e) => setIsp({ ...isp, name: e.target.value })} />
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={isp.address}
              onChange={(e) => setIsp({ ...isp, address: e.target.value })} />
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Customer Care</label>
              <input type="text" value={isp.phone}
                onChange={(e) => setIsp({ ...isp, phone: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Email</label>
              <input type="text" value={isp.email}
                onChange={(e) => setIsp({ ...isp, email: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>GSTIN</label>
              <input type="text" value={isp.gstin}
                onChange={(e) => setIsp({ ...isp, gstin: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Website</label>
              <input type="text" value={isp.website}
                onChange={(e) => setIsp({ ...isp, website: e.target.value })} />
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Customer Details</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>Customer Name</label>
              <input type="text" value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Phone</label>
              <input type="text" value={customer.phone}
                onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={customer.address}
              onChange={(e) => setCustomer({ ...customer, address: e.target.value })} />
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Account No</label>
              <input type="text" value={customer.accountNo}
                onChange={(e) => setCustomer({ ...customer, accountNo: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Customer ID</label>
              <input type="text" value={customer.customerId}
                onChange={(e) => setCustomer({ ...customer, customerId: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Email</label>
              <input type="text" value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Plan Details</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>Plan Name</label>
              <input type="text" value={plan.name}
                onChange={(e) => setPlan({ ...plan, name: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Type</label>
              <select value={plan.type}
                onChange={(e) => setPlan({ ...plan, type: e.target.value })}>
                {PLAN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label>Speed</label>
              <input type="text" value={plan.speed}
                onChange={(e) => setPlan({ ...plan, speed: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Data Limit</label>
              <input type="text" value={plan.dataLimit}
                onChange={(e) => setPlan({ ...plan, dataLimit: e.target.value })} />
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Bill Info</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>Bill No</label>
              <input type="text" value={meta.billNo}
                onChange={(e) => setMeta({ ...meta, billNo: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Bill Date</label>
              <input type="text" value={meta.billDate}
                onChange={(e) => setMeta({ ...meta, billDate: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Due Date</label>
              <input type="text" value={meta.dueDate}
                onChange={(e) => setMeta({ ...meta, dueDate: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group">
              <label>Period From</label>
              <input type="text" value={meta.periodFrom}
                onChange={(e) => setMeta({ ...meta, periodFrom: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Period To</label>
              <input type="text" value={meta.periodTo}
                onChange={(e) => setMeta({ ...meta, periodTo: e.target.value })} />
            </div>
            <div className="input-group">
              <label>Payment Mode</label>
              <select value={meta.paymentMode}
                onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Charges</h3>
          {charges.map((row) => (
            <div key={row.id} className="charge-row">
              <input className="charge-input" type="text" value={row.label}
                onChange={(e) => updateCharge(row.id, { label: e.target.value })} />
              <input className="charge-input charge-input-amount" type="number" value={row.amount}
                onChange={(e) => updateCharge(row.id, { amount: Number(e.target.value) })} />
              <button type="button" className="btn btn-outline"
                onClick={() => removeCharge(row.id)}
                style={{ padding: '6px 8px' }} title="Remove">
                <FaTrash />
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addCharge}>
            <FaPlus /> Add Charge
          </button>

          <h3 style={{ marginTop: 16 }}>Tax & Adjustments</h3>
          <div className="flex-row">
            <div className="input-group">
              <label>CGST (%)</label>
              <input type="number" value={tax.cgst}
                onChange={(e) => setTax({ ...tax, cgst: Number(e.target.value) })} />
            </div>
            <div className="input-group">
              <label>SGST (%)</label>
              <input type="number" value={tax.sgst}
                onChange={(e) => setTax({ ...tax, sgst: Number(e.target.value) })} />
            </div>
            <div className="input-group">
              <label>Previous Balance</label>
              <input type="number" value={tax.previousBalance}
                onChange={(e) => setTax({ ...tax, previousBalance: Number(e.target.value) })} />
            </div>
            <div className="input-group">
              <label>Payment Received</label>
              <input type="number" value={tax.paymentReceived}
                onChange={(e) => setTax({ ...tax, paymentReceived: Number(e.target.value) })} />
            </div>
          </div>

          <div style={{
            marginTop: 18, padding: 14, borderRadius: 10,
            background: '#ecfdf5', border: '1px solid #a7f3d0',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span>Subtotal</span>
              <strong>Rs. {formatINR(totals.subtotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginTop: 4 }}>
              <span>CGST + SGST</span>
              <strong>Rs. {formatINR(totals.totalTax)}</strong>
            </div>
            <hr style={{ margin: '8px 0', border: 0, borderTop: '1px dashed #6ee7b7' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16 }}>
              <strong>Total Due</strong>
              <strong style={{ color: '#047857' }}>Rs. {formatINR(totals.totalDueRounded)}</strong>
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo /> Reset
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              <FaDownload /> Download PDF
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <FaPrint /> Print
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaWifi style={{ color: activeTpl.accent }} /> Live Preview — {activeTpl.label}
          </h3>
          <BillPreview
            template={template}
            isp={isp}
            customer={customer}
            plan={plan}
            meta={meta}
            charges={charges}
            tax={tax}
            totals={totals}
            accent={activeTpl.accent}
            logo={logo}
          />
        </div>
      </div>
    </div>
  );
};

// ---------- Preview helpers ----------
const Row = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '2px 0' }}>
    <span style={{ color: '#6b7280' }}>{k}</span>
    <span style={{ color: '#111827', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
  </div>
);

const ChargesTable = ({ charges, accent }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginTop: 8 }}>
    <thead>
      <tr style={{ background: accent, color: '#fff' }}>
        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Description</th>
        <th style={{ textAlign: 'right', padding: '6px 8px' }}>Amount (Rs.)</th>
      </tr>
    </thead>
    <tbody>
      {charges.map((c, i) => (
        <tr key={c.id} style={{ background: i % 2 ? '#f8fafc' : '#fff' }}>
          <td style={{ padding: '5px 8px' }}>{c.label}</td>
          <td style={{ padding: '5px 8px', textAlign: 'right' }}>{formatINR(c.amount)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TotalsPanel = ({ totals, tax, accent }) => (
  <div style={{ marginTop: 8, marginLeft: 'auto', maxWidth: 320 }}>
    <Row k="Subtotal" v={`Rs. ${formatINR(totals.subtotal)}`} />
    <Row k={`CGST @ ${tax.cgst}%`} v={`Rs. ${formatINR(totals.cgstAmt)}`} />
    <Row k={`SGST @ ${tax.sgst}%`} v={`Rs. ${formatINR(totals.sgstAmt)}`} />
    <Row k="Current Charges" v={`Rs. ${formatINR(totals.currentCharges)}`} />
    {Number(tax.previousBalance) !== 0 && <Row k="Previous Balance" v={`Rs. ${formatINR(tax.previousBalance)}`} />}
    {Number(tax.paymentReceived) !== 0 && <Row k="Payment Received" v={`- Rs. ${formatINR(tax.paymentReceived)}`} />}
    <div style={{
      background: accent, color: '#fff', padding: '8px 10px',
      display: 'flex', justifyContent: 'space-between',
      fontWeight: 700, fontSize: 14, marginTop: 6,
    }}>
      <span>Total Due</span>
      <span>Rs. {formatINR(totals.totalDueRounded)}</span>
    </div>
  </div>
);

const LogoImg = ({ logo, size = 40, bg = '#fff' }) => (
  logo ? (
    <div style={{ width: size, height: size, background: bg, borderRadius: 6, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <img src={logo} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
    </div>
  ) : null
);

const BillPreview = ({ template, isp, customer, plan, meta, charges, tax, totals, accent, logo }) => {
  const containerBase = {
    background: '#fff', borderRadius: 8, padding: 16,
    border: '1px solid #e5e7eb', marginTop: 10, color: '#111827',
  };

  if (template === 'classic') {
    return (
      <div style={containerBase}>
        <div style={{ background: accent, color: '#fff', padding: '12px', margin: '-16px -16px 12px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <LogoImg logo={logo} size={46} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{isp.name}</div>
              <div style={{ fontSize: 11.5, opacity: 0.9 }}>{isp.address}</div>
              <div style={{ fontSize: 11.5, opacity: 0.9 }}>{isp.phone} | {isp.email}</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>INTERNET BILL</div>
            <div style={{ fontSize: 11 }}>GSTIN: {isp.gstin}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Bill To</div>
            <Row k="Name" v={customer.name} />
            <Row k="A/C No" v={customer.accountNo} />
            <Row k="Cust ID" v={customer.customerId} />
            <Row k="Phone" v={customer.phone} />
          </div>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Bill Details</div>
            <Row k="Bill No" v={meta.billNo} />
            <Row k="Date" v={meta.billDate} />
            <Row k="Due Date" v={meta.dueDate} />
            <Row k="Period" v={`${meta.periodFrom} - ${meta.periodTo}`} />
            <Row k="Plan" v={`${plan.name} (${plan.speed})`} />
          </div>
        </div>
        <ChargesTable charges={charges} accent={accent} />
        <TotalsPanel totals={totals} tax={tax} accent={accent} />
      </div>
    );
  }

  if (template === 'modern') {
    return (
      <div style={containerBase}>
        <div style={{ background: accent, color: '#fff', padding: '14px 12px', margin: '-16px -16px 12px', textAlign: 'center', position: 'relative' }}>
          {logo && <div style={{ position: 'absolute', left: 12, top: 12 }}><LogoImg logo={logo} size={46} /></div>}
          <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: 0.5 }}>{isp.name.toUpperCase()}</div>
          <div style={{ fontSize: 11.5, opacity: 0.92 }}>{isp.address}</div>
          <div style={{ fontSize: 11.5, opacity: 0.92 }}>{isp.phone} • {isp.email} • {isp.website}</div>
          <div style={{ fontWeight: 700, fontSize: 12.5, marginTop: 4 }}>INTERNET BILL — {meta.billNo}</div>
        </div>
        <div style={{ fontWeight: 700 }}>{customer.name}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{customer.address}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>A/C {customer.accountNo} • Cust ID {customer.customerId} • {customer.phone}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Plan: {plan.name} ({plan.type}, {plan.speed}, {plan.dataLimit})</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Period: {meta.periodFrom} to {meta.periodTo} | Due: {meta.dueDate} | GSTIN: {isp.gstin}</div>
        <ChargesTable charges={charges} accent={accent} />
        <TotalsPanel totals={totals} tax={tax} accent={accent} />
      </div>
    );
  }

  if (template === 'minimal') {
    return (
      <div style={containerBase}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <LogoImg logo={logo} size={44} bg="#f3f4f6" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{isp.name}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>
              {isp.address} | {isp.phone} | {isp.email}
            </div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>GSTIN: {isp.gstin}</div>
          </div>
        </div>
        <hr style={{ margin: '8px 0', borderTop: '1px solid #111827' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
          <span>INTERNET BILL</span><span>{meta.billNo}</span>
        </div>
        <div style={{ marginTop: 8 }}>
          <Row k="Customer" v={customer.name} />
          <Row k="A/C No" v={`${customer.accountNo} (Cust ID ${customer.customerId})`} />
          <Row k="Address" v={customer.address} />
          <Row k="Plan" v={`${plan.name} • ${plan.speed} • ${plan.dataLimit}`} />
          <Row k="Period" v={`${meta.periodFrom} to ${meta.periodTo}`} />
          <Row k="Bill / Due" v={`${meta.billDate} / ${meta.dueDate}`} />
        </div>
        <ChargesTable charges={charges} accent={accent} />
        <TotalsPanel totals={totals} tax={tax} accent={accent} />
      </div>
    );
  }

  if (template === 'sidebar') {
    return (
      <div style={{ ...containerBase, padding: 0, overflow: 'hidden', display: 'flex' }}>
        <div style={{ background: accent, color: '#fff', padding: 14, width: 160, minWidth: 160 }}>
          {logo && <div style={{ marginBottom: 10 }}><LogoImg logo={logo} size={56} /></div>}
          <div style={{ fontWeight: 700, fontSize: 13 }}>{isp.name}</div>
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.9 }}>{isp.address}</div>
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>{isp.phone}</div>
          <div style={{ fontSize: 11, opacity: 0.9 }}>{isp.email}</div>
          <div style={{ marginTop: 12, fontWeight: 700, fontSize: 11 }}>BILL NO</div>
          <div style={{ fontSize: 12 }}>{meta.billNo}</div>
          <div style={{ marginTop: 8, fontWeight: 700, fontSize: 11 }}>DUE DATE</div>
          <div style={{ fontSize: 12 }}>{meta.dueDate}</div>
          <div style={{ marginTop: 12, fontWeight: 700, fontSize: 11 }}>TOTAL DUE</div>
          <div style={{ fontWeight: 800, fontSize: 14 }}>Rs. {formatINR(totals.totalDueRounded)}</div>
        </div>
        <div style={{ padding: 14, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Internet Bill</div>
          <Row k="Customer" v={customer.name} />
          <Row k="A/C No" v={customer.accountNo} />
          <Row k="Address" v={customer.address} />
          <Row k="Plan" v={`${plan.name} (${plan.speed}, ${plan.dataLimit})`} />
          <Row k="Period" v={`${meta.periodFrom} - ${meta.periodTo}`} />
          <Row k="GSTIN" v={isp.gstin} />
          <ChargesTable charges={charges} accent={accent} />
        </div>
      </div>
    );
  }

  // compact
  return (
    <div style={containerBase}>
      <div style={{ borderTop: `3px solid ${accent}`, paddingTop: 8, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <LogoImg logo={logo} size={44} bg="#f3f4f6" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{isp.name}</div>
            <div style={{ fontSize: 11.5, color: '#6b7280' }}>{isp.address} • {isp.phone} • GSTIN {isp.gstin}</div>
          </div>
        </div>
        <div style={{ color: accent, fontWeight: 700 }}>TAX INVOICE — {meta.billNo}</div>
      </div>
      <div style={{ background: '#f8fafc', padding: 10, borderRadius: 6, marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <Row k="Customer" v={customer.name} />
          <Row k="A/C No" v={customer.accountNo} />
          <Row k="Cust ID" v={customer.customerId} />
          <Row k="Phone" v={customer.phone} />
          <Row k="Address" v={customer.address} />
        </div>
        <div>
          <Row k="Bill Date" v={meta.billDate} />
          <Row k="Due Date" v={meta.dueDate} />
          <Row k="Period" v={`${meta.periodFrom} - ${meta.periodTo}`} />
          <Row k="Plan" v={`${plan.name} (${plan.speed})`} />
          <Row k="Payment" v={meta.paymentMode} />
        </div>
      </div>
      <ChargesTable charges={charges} accent={accent} />
      <TotalsPanel totals={totals} tax={tax} accent={accent} />
    </div>
  );
};

export default InternetBillGenerator;
